import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  CpfField,
  FormActions,
  FormGrid,
  SelectField,
  TextField,
  TextareaField,
  onlyDigits,
} from "@atlas/ui/web";
import { cpfValido } from "@atlas/domain";
import { atlas } from "../../../../lib/sdk";
import type { BancoPerfil, BancoPerfilPreset, BancoUsuarioInput } from "@atlas/sdk";
import {
  BANCO_PRESETS,
  BANCO_PRESET_LABELS,
  bancoGruposDoModo,
  bancoPresetsDoModo,
  detectarBancoPreset,
  type BancoPerfilLabel,
} from "../../../../lib/banco-perms";

/**
 * Lista do servidor, ou vazia — inclusive quando o campo NAO e' uma lista.
 *
 * `?? []` nao basta: o que quebrou esta tela foi um campo presente com o tipo
 * errado (a string `"{}"`), e `"{}" ?? []` devolve a string. O tipo do SDK
 * promete `string[]`, entao o TypeScript nao ajuda aqui — a checagem tem que
 * ser em tempo de execucao.
 */
function listaOuVazio(v: unknown): string[] {
  return Array.isArray(v) ? v.map((x) => String(x)) : [];
}

export function BancoUsuariosForm() {
  const params = useParams<{ id?: string }>();
  const isNovo = params.id === "novo" || !params.id;
  const id = isNovo ? undefined : params.id;
  const nav = useNavigate();
  const qc = useQueryClient();

  const existing = useQuery({
    queryKey: ["banco", "usuario", id],
    queryFn: () => atlas.banco.getUsuario(id!),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });

  // Nome do banco LOGADO. Substitui o default "46177 - DELTA GLOBAL SOCIEDADE
  // DE CREDITO DIRETO S A", que era uma string fixa: todo operador, de qualquer
  // instituicao, nascia pertencendo a uma empresa ficticia. Mesma queryKey do
  // layout, entao aproveita o cache em vez de pedir de novo.
  const meQ = useQuery({
    queryKey: ["banco", "me"],
    queryFn: () => atlas.banco.me(),
    staleTime: 60_000,
  });

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  // CPF que se DIGITA (mascarado na tela, enviado so' com digitos). E' campo
  // separado da mascara que o servidor devolve: o antigo `cpfMasked` editavel
  // era a origem do "apaga a formatacao" — o operador digitava o CPF, salvava,
  // e ao reabrir via `***.***.***-**` de volta, porque o valor digitado ia no
  // campo errado e nunca chegava ao banco.
  const [cpf, setCpf] = useState("");
  const [ipsRaw, setIpsRaw] = useState("");
  const [ativo, setAtivo] = useState(true);
  /** `""` = nenhuma. Opcional: operador antigo continua valido sem. */
  const [subOrganizacaoId, setSubOrganizacaoId] = useState("");
  const [permissoes, setPermissoes] = useState<string[]>([...BANCO_PRESETS.operador]);
  const [presetNome, setPresetNome] = useState("");
  const [senha, setSenha] = useState("");
  const [senha2, setSenha2] = useState("");
  const [erroCampo, setErroCampo] = useState<{ cpf?: string; senha?: string }>({});
  // Etapa 2: quando clicar Salvar com config personalizada em criacao, abre
  // overlay dedicado pra nomear o preset ANTES de salvar. UX alinhado ao
  // padrao da prefeitura (colega refinou 22/07/2026).
  const [etapaNomePreset, setEtapaNomePreset] = useState(false);

  // Presets customizados do banco (nomeados). Aparecem no dropdown junto com
  // os presets nativos. Carrega uma vez no mount — invalida no onSuccess.
  const presetsQ = useQuery({
    queryKey: ["banco", "perfil-presets"],
    queryFn: () => atlas.banco.perfilPresetsBanco(),
    staleTime: 30_000,
  });
  const presetsCustom: BancoPerfilPreset[] = presetsQ.data?.presets ?? [];

  // Opcoes do campo Sub organizacao: so' as ATIVAS do banco. A que o operador
  // ja tem entra mesmo desativada, marcada — senao o select a mostraria como
  // "Nenhuma" e salvar o nome do operador o desvincularia sem ninguem pedir.
  const subOrgsQ = useQuery({
    queryKey: ["banco", "sub-organizacoes", "ativo", "opcoes"],
    queryFn: () => atlas.banco.listSubOrganizacoes({ situacao: "ativo" }),
    staleTime: 30_000,
  });
  const opcoesSubOrganizacao = useMemo(() => {
    const ativas = (subOrgsQ.data?.subOrganizacoes ?? []).map((s) => ({ value: s.id, label: s.nome }));
    const atual = existing.data?.usuario;
    if (atual?.subOrganizacaoId && !ativas.some((o) => o.value === atual.subOrganizacaoId)) {
      ativas.push({ value: atual.subOrganizacaoId, label: `${atual.subOrganizacaoNome ?? atual.subOrganizacaoId} (inativa)` });
    }
    return [{ value: "", label: "Nenhuma" }, ...ativas];
  }, [subOrgsQ.data, existing.data]);

  /** Mascara vinda do servidor — READ-ONLY, so' pra mostrar qual CPF esta
   *  gravado enquanto o campo de digitacao esta vazio. */
  const [cpfMascaraSalva, setCpfMascaraSalva] = useState("");
  const [temSenha, setTemSenha] = useState(false);
  const [trocandoSenha, setTrocandoSenha] = useState(false);
  /** Preset escolhido. `null` enquanto a carga inicial nao aconteceu — e' o que
   *  distingue "ainda nao sei" de "o usuario escolheu nada". */
  const [presetEscolhido, setPresetEscolhido] = useState<string | null>(null);

  useEffect(() => {
    if (!existing.data) return;
    const u = existing.data.usuario;
    // Todos os campos com default: o efeito precisa ser TOTAL.
    //
    // `listaOuVazio` e nao `?? []`: o que derrubou esta tela nao foi campo
    // AUSENTE, foi campo com o TIPO errado. A API devolvia `ipsPermitidos` como
    // a string `"{}"` (o driver entrega `text[]` como literal do Postgres — ver
    // `lerArrayPg` em db/repos.ts), e `"{}" ?? []` e' a propria string: o
    // `.join` seguinte estourava "is not a function" e a rota inteira morria.
    //
    // A causa foi corrigida no servidor. Isto fica como o que a tela deve a si
    // mesma: payload com forma inesperada vira campo vazio, nao tela branca.
    setNome(u.nome ?? "");
    setEmail(u.email ?? "");
    setCpfMascaraSalva(u.cpfMasked ?? "");
    setCpf("");
    setIpsRaw(listaOuVazio(u.ipsPermitidos).join("\n"));
    setAtivo(u.ativo ?? true);
    setSubOrganizacaoId(u.subOrganizacaoId ?? "");
    setTemSenha(!!u.temSenha);
    setTrocandoSenha(false);
    // Mesma armadilha do `ipsPermitidos`, com consequencia PIOR porque
    // silenciosa: `permissoes` tambem chegava como string, `Array.isArray`
    // respondia `false`, e a tela caia no ramo do preset — mostrando (e, ao
    // salvar, gravando) as permissoes do preset por cima das que o admin tinha
    // escolhido. Nenhum erro em tela. `listaOuVazio` fecha os dois casos.
    const doServidor = listaOuVazio(u.permissoes);
    const perms =
      doServidor.length > 0
        ? doServidor
        : [...(BANCO_PRESETS[u.perfil as BancoPerfilLabel] ?? [])];
    setPermissoes(perms);
    // O vinculo com o preset agora e' PERSISTIDO (`presetKey`). Antes ele era
    // readivinhado por coincidencia de permissoes, entao um preset salvo
    // deixava de ser reconhecido assim que alguem mexia numa caixa.
    setPresetEscolhido(u.presetKey ?? detectarBancoPreset(perms));
  }, [existing.data]);

  /**
   * A organizacao do operador e' o BANCO LOGADO, e so' isso — nao e' escolha.
   *
   * Valor DERIVADO, nao estado: nao ha `setOrganizacao`, entao nao existe
   * caminho na tela que o altere. O campo abaixo e' `disabled` por cima disso,
   * mas o que garante de verdade e' o servidor, que derruba `organizacao` do
   * corpo e grava sempre o nome do banco do JWT — input desabilitado nao impede
   * ninguem de chamar a API direto.
   *
   * Enquanto `me()` nao responde, mostra o que veio do registro para o campo nao
   * piscar vazio na edicao.
   */
  const organizacao = meQ.data?.nome ?? existing.data?.usuario.organizacao ?? "";

  // #314: o modo do credor decide o que a tela oferece. Uma consignataria de
  // servicos nao ve preset nem caixa de credito; um banco nao ve beneficios.
  // Recurso fora do modo nao seria alcancado de qualquer forma — a API confere
  // o modo — mas oferece-lo seria prometer um acesso que nao existe.
  const modo = meQ.data?.modo_portal;
  const gruposDoModo = useMemo(() => bancoGruposDoModo(modo), [modo]);
  const recursosDoModo = useMemo(() => gruposDoModo.flatMap((g) => g.recursos.map((r) => r.key)), [gruposDoModo]);
  const presetsOferecidos = useMemo(() => {
    const doModo = bancoPresetsDoModo(modo);
    return BANCO_PRESET_LABELS.filter((p) => doModo.includes(p.value));
  }, [modo]);
  // Operador NOVO de quem so' opera beneficios parte do preset dele, nao do
  // `operador` de credito — que nao teria caixa nenhuma marcada nesta tela.
  const [padraoDoModoAplicado, setPadraoDoModoAplicado] = useState(false);
  useEffect(() => {
    if (!isNovo || padraoDoModoAplicado || !meQ.isSuccess) return;
    setPadraoDoModoAplicado(true);
    if (modo === "operadora") setPermissoes([...BANCO_PRESETS.operadora]);
  }, [isNovo, padraoDoModoAplicado, meQ.isSuccess, modo]);

  const supervisor = permissoes.includes("*");
  const perfilDetectado = useMemo<BancoPerfilLabel>(() => detectarBancoPreset(permissoes), [permissoes]);
  // `presetEscolhido` e' ESTADO DO USUARIO, nao derivado das permissoes.
  //
  // Antes um `useEffect([perfilDetectado])` o reescrevia toda vez que
  // `permissoes` mudava — inclusive no mesmo tick em que `aplicarPreset`
  // acabava de defini-lo. Efeito: escolher um preset salvo mudava as caixas e
  // JOGAVA a selecao de volta para "— escolher —", e como `presetSalvoAtivo`
  // dependia dela, o travamento das caixas nunca acontecia.
  const presetCorrente = presetEscolhido ?? perfilDetectado;
  // Preset SALVO ativo -> permissoes TRAVADAS. Padrao da prefeitura.
  const presetSalvoAtivo = presetsCustom.some((p) => p.key === presetCorrente);
  // Nomear preset e' exigido so' quando a configuracao e' NOVA — ou seja, quando
  // ela nao veio de um preset que ja existe.
  //
  // `perfilDetectado` sozinho nao serve: ele compara as caixas com os presets
  // NATIVOS, e um preset salvo quase nunca bate com algum deles. Resultado, ao
  // escolher um preset ja salvo: `perfilDetectado === "personalizado"` e a tela
  // abria o modal pedindo um nome para o preset — para uma configuracao que ja
  // tem nome, e que o usuario acabou de escolher pelo nome.
  const exigePresetNome = isNovo && perfilDetectado === "personalizado" && !presetSalvoAtivo;

  function aplicarPreset(v: string) {
    setPresetEscolhido(v);
    const custom = presetsCustom.find((p) => p.key === v);
    if (custom) { setPermissoes([...custom.permissoes]); return; }
    setPermissoes([...(BANCO_PRESETS[v as BancoPerfilLabel] ?? [])]);
  }
  function togglePermissao(key: string) {
    // Mexer numa caixa desfaz o vinculo com o preset salvo — a config passa a
    // ser do usuario, e continuar exibindo o nome do preset seria mentira.
    setPresetEscolhido(null);
    if (supervisor) {
      // Desmarcar uma caixa do "*" vira a lista explicita — so' do modo do credor.
      setPermissoes(recursosDoModo.filter((k) => k !== key));
      return;
    }
    setPermissoes((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  }

  /** Valida CPF e senha. Devolve `true` quando pode salvar. */
  function validar(): boolean {
    const erros: { cpf?: string; senha?: string } = {};
    const dig = onlyDigits(cpf);
    // Na edicao o campo vazio significa "mantem o CPF atual".
    const cpfObrigatorio = isNovo || dig.length > 0;
    if (cpfObrigatorio) {
      if (dig.length !== 11) erros.cpf = "CPF deve ter 11 dígitos.";
      else if (!cpfValido(dig)) erros.cpf = "CPF inválido — confira os dígitos.";
    }
    const senhaObrigatoria = isNovo || trocandoSenha;
    if (senhaObrigatoria) {
      if (senha.length < 8) erros.senha = "A senha deve ter no mínimo 8 caracteres.";
      else if (senha !== senha2) erros.senha = "As senhas não conferem.";
    }
    setErroCampo(erros);
    return Object.keys(erros).length === 0;
  }

  const save = useMutation({
    mutationFn: async () => {
      const dig = onlyDigits(cpf);
      const body: BancoUsuarioInput = {
        id,
        nome,
        email,
        // `cpf` (digitos), NAO `cpfMasked`: era exatamente este o campo que
        // faltava mandar, e por isso a coluna ficava vazia para todo operador.
        ...(dig.length === 11 ? { cpf: dig } : {}),
        perfil: perfilDetectado as BancoPerfil,
        permissoes,
        // `null` desvincula quando o usuario personalizou as caixas.
        presetKey: presetsCustom.some((p) => p.key === presetCorrente) ? presetCorrente : null,
        ipsPermitidos: ipsRaw.split(/[\n,]/).map((s) => s.trim()).filter(Boolean),
        ativo,
        // `null` desvincula — "Nenhuma" no select e' escolha, nao ausencia.
        subOrganizacaoId: subOrganizacaoId || null,
        // So manda o nome do preset quando for criar com config personalizada.
        presetNome: exigePresetNome ? presetNome.trim() : undefined,
        // Na criacao a senha vai junto. Na edicao ela tem rota propria, pra que
        // salvar o nome de alguem nao possa mexer na credencial por acidente.
        ...(isNovo ? { password: senha } : {}),
      };
      const r = await atlas.banco.upsertUsuario(body);
      if (!isNovo && trocandoSenha && senha) {
        await atlas.banco.redefinirSenhaUsuario(r.usuario.id, senha);
      }
      return r;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["banco", "usuarios"] });
      qc.invalidateQueries({ queryKey: ["banco", "perfil-presets"] });
      if (id) qc.invalidateQueries({ queryKey: ["banco", "usuario", id] });
      nav("/banco/cadastros/usuarios");
    },
  });

  const totalMarcadas = supervisor ? recursosDoModo.length : permissoes.length;

  // Carga falhou: dizer isso. Antes a tela seguia com o formulario VAZIO, e
  // salvar sobrescreveria o registro com campos em branco.
  if (id && existing.isError) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 560 }}>
        <h1 style={{ margin: 0, fontSize: "1.4rem" }}>Operador não encontrado</h1>
        <p style={{ color: "var(--text-muted)", margin: 0, fontSize: 14 }}>
          {existing.error instanceof Error
            ? existing.error.message
            : "Não foi possível carregar este operador."}
        </p>
        <div>
          <Button variant="ghost" onClick={() => nav("/banco/cadastros/usuarios")}>
            ← Voltar para Operadores
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (!validar()) return;
        if (exigePresetNome) { setEtapaNomePreset(true); } else { save.mutate(); }
      }}
      style={{ display: "flex", flexDirection: "column", gap: 20, width: "100%" }}
    >
      {etapaNomePreset ? (
        <div
          onClick={() => setEtapaNomePreset(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
              borderRadius: 12, padding: 24, width: "min(480px, 92vw)",
              display: "flex", flexDirection: "column", gap: 12,
            }}
          >
            <h3 style={{ margin: 0 }}>Nome do preset</h3>
            <p style={{ color: "var(--text-muted)", fontSize: 13, margin: 0 }}>
              Você personalizou as permissões (<b>{totalMarcadas}</b> marcada(s)). Dê um nome pra essa
              configuração — fica salva como preset e vira opção reutilizável pra outros usuários da consignatária.
            </p>
            <TextField
              label="Nome do preset"
              value={presetNome}
              onChange={(e) => setPresetNome(e.target.value)}
              placeholder="ex.: Analista de crédito"
              autoFocus
            />
            {save.error ? (
              <div style={{ color: "var(--danger-500)", fontSize: 13 }}>
                {save.error instanceof Error ? save.error.message : "Erro ao salvar"}
              </div>
            ) : null}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
              <Button variant="ghost" type="button" onClick={() => setEtapaNomePreset(false)}>Voltar</Button>
              <Button type="button" onClick={() => save.mutate()} disabled={save.isPending || presetNome.trim().length < 2}>
                {save.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Cadastros • Operadores
        </span>
        {/* "Editar Operador", e nao "Editar U-8-001": o id interno nao diz nada
            a quem esta na tela, e quem o cadastrou reconhece a pessoa pelo nome
            e pelo login, que aparecem logo abaixo. */}
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>{id ? "Editar Operador" : "Novo operador"}</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 780, fontSize: 13 }}>
          Escolha um preset como ponto de partida ou marque/desmarque caixa a caixa para customizar o que este usuário pode ver e fazer.
        </p>
      </header>

      <section
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <FormGrid cols={2}>
          <TextField label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} required />
          <TextField
            label="Login (e-mail)"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            hint="É com este e-mail que o operador entra no portal."
            required
          />
          <CpfField
            label="CPF"
            value={cpf}
            onChange={(e) => { setCpf(e.target.value); setErroCampo((p) => ({ ...p, cpf: undefined })); }}
            error={erroCampo.cpf}
            placeholder={isNovo ? "000.000.000-00" : cpfMascaraSalva || "000.000.000-00"}
            hint={isNovo ? undefined : "Deixe em branco para manter o CPF atual."}
            required={isNovo}
          />
          <TextField
            label="Organização"
            value={organizacao}
            readOnly
            disabled
            hint="É a consignatária em que você está logado — o operador pertence a ela."
          />
          {/* Grid de 2 colunas: Situacao vem antes para que Sub organizacao
              caia na coluna da direita, logo ABAIXO de Organizacao. */}
          <SelectField
            label="Situação"
            value={ativo ? "1" : "0"}
            onChange={(e) => setAtivo(e.target.value === "1")}
            options={[
              { value: "1", label: "Ativo" },
              { value: "0", label: "Inativo" },
            ]}
          />
          <SelectField
            label="Sub organização"
            value={subOrganizacaoId}
            onChange={(e) => setSubOrganizacaoId(e.target.value)}
            options={opcoesSubOrganizacao}
            hint="Opcional. Cadastre as opções em Cadastros › Sub organizações."
          />
        </FormGrid>

        {/* Acesso. Ate 15/09/2026 o operador nao tinha senha em camada nenhuma:
            era cadastrado, aparecia na grid, e ao tentar entrar recebia
            "Credenciais invalidas" — indistinguivel de senha errada. */}
        <div
          style={{
            borderTop: "1px solid var(--border)",
            paddingTop: 16,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Acesso ao portal</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              {isNovo
                ? "Defina uma senha inicial. O operador será obrigado a trocá-la no primeiro acesso."
                : temSenha
                  ? "Este operador já tem acesso. Redefina a senha apenas se ele a perdeu."
                  : "Este operador ainda NÃO tem senha e não consegue entrar."}
            </div>
          </div>

          {!isNovo && !trocandoSenha ? (
            <div>
              <Button size="sm" variant="ghost" type="button" onClick={() => setTrocandoSenha(true)}>
                {temSenha ? "Redefinir senha" : "Definir senha de acesso"}
              </Button>
            </div>
          ) : null}

          {isNovo || trocandoSenha ? (
            <FormGrid cols={2}>
              <TextField
                label={isNovo ? "Senha inicial" : "Nova senha"}
                type="password"
                value={senha}
                onChange={(e) => { setSenha(e.target.value); setErroCampo((p) => ({ ...p, senha: undefined })); }}
                autoComplete="new-password"
                hint="Mínimo 8 caracteres."
                error={erroCampo.senha}
                required
              />
              <TextField
                label="Confirmar senha"
                type="password"
                value={senha2}
                onChange={(e) => { setSenha2(e.target.value); setErroCampo((p) => ({ ...p, senha: undefined })); }}
                autoComplete="new-password"
                required
              />
            </FormGrid>
          ) : null}
        </div>

        <TextareaField
          label="IPs permitidos (allowlist)"
          value={ipsRaw}
          onChange={(e) => setIpsRaw(e.target.value)}
          placeholder="Ex: 189.45.10.0/24, 200.150.20.42"
          hint="Um por linha ou separados por vírgula. Vazio = qualquer IP."
        />
      </section>

      <section
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Permissões</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              Perfil atual: <b>{perfilDetectado}</b> · <b>{totalMarcadas}</b> {supervisor ? "(todas via *)" : "marcada(s)"}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            {/* Lado 1 — presets nativos (built-in). */}
            <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span style={{ fontSize: 10, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>Presets</span>
              <select
                value={presetsOferecidos.some((p) => p.value === presetCorrente) ? presetCorrente : ""}
                onChange={(e) => { if (e.target.value) aplicarPreset(e.target.value); }}
                style={{
                  minWidth: 130, padding: "8px 10px", borderRadius: 8,
                  background: "var(--bg-elev-2)", color: "var(--text)",
                  border: "1px solid var(--border-strong)", fontSize: 13,
                }}
              >
                <option value="" disabled>— escolher —</option>
                {presetsOferecidos.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </label>

            {/* Lado 2 — presets salvos (customizados). */}
            <label style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span style={{ fontSize: 10, letterSpacing: "0.06em", fontWeight: 700, color: "var(--gold-500)", textTransform: "uppercase" }}>Presets salvos</span>
              <select
                value={presetsCustom.some((p) => p.key === presetCorrente) ? presetCorrente : ""}
                onChange={(e) => { if (e.target.value) aplicarPreset(e.target.value); }}
                disabled={presetsCustom.length === 0}
                style={{
                  minWidth: 140, padding: "8px 10px", borderRadius: 8,
                  background: "var(--bg-elev-2)", color: "var(--text)",
                  border: "1px solid var(--border-strong)", fontSize: 13,
                }}
              >
                <option value="" disabled>{presetsCustom.length === 0 ? "nenhum ainda" : "— escolher —"}</option>
                {presetsCustom.map((p) => (
                  <option key={p.key} value={p.key}>{p.nome}</option>
                ))}
              </select>
            </label>

            <div style={{ display: "flex", gap: 6, alignSelf: "flex-end" }}>
              <Button size="sm" variant="ghost" type="button" disabled={presetSalvoAtivo} onClick={() => { setPresetEscolhido(null); setPermissoes(["*"]); }}>Marcar tudo</Button>
              <Button size="sm" variant="ghost" type="button" disabled={presetSalvoAtivo} onClick={() => { setPresetEscolhido(null); setPermissoes([]); }}>Limpar</Button>
            </div>
          </div>
        </div>

        {presetSalvoAtivo ? (
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            As caixas estão travadas porque um preset salvo está aplicado. Escolha um preset nativo
            (ou use “Marcar tudo”/“Limpar”) para editar caixa a caixa.
          </div>
        ) : null}

        <div style={{
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 12, maxHeight: 420, overflowY: "auto",
          padding: 12, background: "var(--bg-elev-2)", borderRadius: 10,
          opacity: presetSalvoAtivo ? 0.6 : 1,
        }}>
          {gruposDoModo.map((g) => (
            <div key={g.titulo} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 700, color: "var(--gold-500)", textTransform: "uppercase", marginBottom: 4 }}>
                {g.titulo}
              </div>
              {g.recursos.map((r) => {
                const marcada = supervisor || permissoes.includes(r.key);
                return (
                  <label
                    key={r.key}
                    style={{
                      display: "flex", alignItems: "flex-start", gap: 8,
                      padding: "6px 8px", borderRadius: 6, cursor: presetSalvoAtivo ? "not-allowed" : "pointer",
                      background: marcada ? "color-mix(in srgb, var(--emerald-500) 12%, transparent)" : "transparent",
                      border: marcada ? "1px solid var(--emerald-500)" : "1px solid var(--border)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={marcada}
                      disabled={presetSalvoAtivo}
                      onChange={() => togglePermissao(r.key)}
                      style={{ marginTop: 3 }}
                    />
                    <span style={{ flex: 1 }}>
                      <span style={{ fontSize: 13, fontWeight: 600 }}>{r.label}</span>
                      {r.descricao ? (
                        <span style={{ display: "block", fontSize: 11, color: "var(--text-muted)" }}>{r.descricao}</span>
                      ) : null}
                      <code style={{ fontSize: 10, color: "var(--text-dim)" }}>{r.key}</code>
                    </span>
                  </label>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      {save.error ? (
        <div style={{ color: "var(--danger-500)", fontSize: 13 }}>
          {save.error instanceof Error ? save.error.message : "Erro ao salvar"}
        </div>
      ) : null}

      <FormActions>
        <Button variant="ghost" type="button" onClick={() => nav("/banco/cadastros/usuarios")}>Cancelar</Button>
        <Button type="submit" disabled={save.isPending}>{save.isPending ? "Salvando..." : "Salvar"}</Button>
      </FormActions>
    </form>
  );
}
